/**
 * @title locator.js: Feature level module
 * @description The module used for the 'locator'
 * feature which encapsulates the locator page.
 * @author kmedley
 */
define([], function() {
  let Locator = {
    /**
     * The Microsoft Maps that get instantiated in Locator.init_bing_map.
     */
    map: {},
    /**
     * The Microsoft search module that gets lazy loaded in Locator.search
     */
    search_manager: {},
    /**
     * The amount of times requests failed to BING
     */
    error_count: 0,
    /**
     * The Microsoft Auto Suggest module loaded on locations_perform_execute (needs to reference Locator.map).
     */
    auto_suggest_manager: undefined,
    /**
     * The "pop up" that displays inside the BING map over the pin
     */
    infobox: undefined,
    /**
     * The tires flag that determines if we need the select warehouse button
     */
    is_tires: window.location.pathname == '/WarehouseTiresLocatorView',
    /**
     * The pickup at warehouse flag that determine if we need the select warehouse button
     */
    is_pickup_warehouse: window.location.pathname == '/WarehousePickupLocatorView',
    /**
     * The pharmacy flag determines if we need to display pharmacy locations
     */
    is_pharmacy: window.location.pathname.toLowerCase().indexOf('/pharmacy') !== -1,
    /**
     * The transfer warehouse flag determines if we need the select warehouse button
     */
    is_transfer_warehouse: window.location.pathname == '/transfer-warehouse-location',
    /**
     * The is_details function determines if we're on the warehouse details page.
     * @return {Boolean}
     */
    is_details: function() {
      return document.getElementById('warehouse') ? true : false;
    },
    /**
     * The array of warehouse objects returned from calling AjaxWarehouseBrowseLookupView
     */
    warehouses: [],
    /**
     * The geolocation of the user as an object {latitude:number, longitude:number}
     */
    lat_long: {},
    /**
     * This tells the application to first use the search term set by the query string,
     * cookie, or geolocation on page load. Any subsequent searches use the input field value.
     */
    isFirstLoad: true,
    /**
     * This tells the application to show the view more button at the end 
     * if there are more results that can be shown for the input location.
     */
    hasMoreWarehouses: false,
    /**
     * Additional warehouses that need to be shown if the "view more" button is clicked 
     */
    additionalWarehouses: 0,
    
    /**
     * Fires the LocatorLoaded event to tell the listener defined
     * on LocatorBody.jsp to execute the initialize function
     * @return {void}
     */
    loaded: function() {
      COSTCO.Global.notify('LocatorLoaded');
    },
    /**
     * Series of state-setting functions happen to set up the locator.
     * Sets the application type, the filter state using
     * the querystrings we arived here with and the search input box.
     * Then sets an event listener onresize (and triggers) to build the bing
     * map using the appropriate container based on the viewport width.
     * (This is for the warehouse locator landing page)
     * @return {void} [description]
     */
    initialize: function() {
      Locator.set_application_type();
      Locator.set_filter_state(COSTCO.util.get_parameters_as_object());
      Locator.width = $(window).width();
      Locator.height = $(window).height();
      $(window).unbind('resize.locator').on('resize.locator', COSTCO.util.debounce(function() {
        if (Locator.width != $(window).width()) {
          Locator.format_map_container();
          COSTCO.util.safe(Locator.locations_perform_execute,
            'COSTCO.locator.locations_perform_execute')(Locator.get_map_id());
          Locator.affix();
          Locator.width = $(window).width();
        }
      }, 250));
      COSTCO.dialog.initializeReturnFocusListener();
      // Necessary because initialize can be called before the DOM is finished
      // rendering, resulting in #map-container mis-calculating the width in
      // which the map sits. Can cause x-axis overflow. :(
      document.readyState == 'complete'
        ? Locator.onload()
        : $(window).unbind('load.locator').on('load.locator', Locator.onload);
    },
    /**
     * Initializes
     * @return {void}
     */
    onload: function() {
	  Locator.format_map_container();
	  let fn = Locator.is_details() ? Locator.details_perform_execute : Locator.locations_perform_execute
	  COSTCO.util.safe(fn)(Locator.is_details() ? 'bing-map' : Locator.get_map_id());
	  Locator.affix();
	  Locator.expand_warehouse_details_accordion();
    },
    /**
     * This formats the map container for the warehouse details page. Its essentially performs the
     * same steps as Locator.initialize except we don't deal with other application types (tires, pharmacy)
     * (This is for the warehouse details landing page)
     * @return {void}
     */
    initialize_details: function() {
      Locator.width = $(window).width();
      Locator.height = $(window).height();
      $(window).unbind('resize.warehouse').on('resize.warehouse', COSTCO.util.debounce(function() {
        if (Locator.width != $(window).width() || Locator.height != $(window).height()) {
          Locator.format_map_container();
          COSTCO.util.safe(Locator.details_perform_execute,
            'COSTCO.locator.details_perform_execute')('bing-map');
          Locator.width = $(window).width();
          Locator.height = $(window).height();
        }
      }, 250));
      // See Locator.initialize for explanation.
      document.readyState == 'complete'
          ? Locator.onload.call('details')
          : $(window).unbind('load.details').on('load.details', Locator.onload.bind('details'));
    },
    /**
     * Affix map to top once the map reaches the top of the screen. Need to set
     * .affix-bottom { position: absolute; } in the scss to retain it.
     * @return {void}
     */
    affix: function() {
      $('#mobile-map-container').affix({
        offset: {
          top: function() {
            // Calculate the height of elements above the mobile-map-container
            let headerHeight = $('#header-widget').outerHeight(true);
            let formHeight = $('#list-column form').outerHeight(true);
            let titleHeight = $(window).width() > 667 ?
              $('#title-container').outerHeight(true) : 0;
            let crumbHeight = $(window).width() > 667 ?
              $('.crumbs').outerHeight(true) : 0;
            let pageOffset = 20;
            return headerHeight + formHeight + pageOffset + titleHeight + crumbHeight;
          },
          bottom: function() {
            // Calculate the height of the footer plus the padding/border offset
            let paddingOffset = 11;
            let footerHeight = $('#footer-widget').outerHeight(true);
            return footerHeight + paddingOffset;
          },
        },
      });
      $('#mobile-map-container').affix('checkPosition');
    },
    /**
     * Set the application type. If this is a tires flow,
     * We'll get buttons to select a warehouse, if its pharmacy
     * prescription transfer, we'll get buttons to select a warehouse,
     * otherwise its the normal locator landing page
     * @return {void}
     */
    set_application_type: function() {
      if (Locator.is_tires) {
        $('h1.tires, #tires-message, .tires-espot').removeClass('hide');
      } else if (Locator.is_pharmacy) { // just set the filter + normal behavior
        $('#locator_hasPharmacy').prop('checked', true);
        $('h1.title, .switch').removeClass('hide');
      } else if (Locator.is_transfer_warehouse) {
        $('h1.pharmacy, #pharmacy-message').removeClass('hide');
      } else if (Locator.is_pickup_warehouse) {
        $('h1.pickup, #pickup-message').removeClass('hide');
      } else {
        $('h1.title, .switch').removeClass('hide');
      }
    },
    /**
     * Returns the id of the appropriate map based on the viewport width.
     * @return {string}
     */
    get_map_id: function() {
      return $(window).width() < 769 ?
        'mobile-bing-map' : 'bing-map';
    },
    /**
     * Uses the querystrings we arrived here with and sets the checkboxes
     * of department filters based on their values.
     * @param  {Object} obj The query string object.
     * @return {void}
     */
    set_filter_state: function(obj) {
      Object.keys(obj).forEach(function(key) {
        obj[key] == 'true' || obj[key] == true ?
          $('#locator_' + key + ', ' + '#locator_' + key + '_modal')
          .prop('checked', true) :
          $('#locator_' + key + ', ' + '#locator_' + key + '_modal')
          .prop('checked', false);
      });
    },
    /**
     * Returns the department filter checkbox values current state
     * as an object.
     * @param {Boolean} isModal optional param to get values of checkboxes
     *                          from filter modal
     * @return {object}
     */
    get_filter_state: function(isModal) {
      return {
        hasGas: $('#locator_hasGas' + (isModal ? '_modal' : '')).prop('checked'),
        hasTires: $('#locator_hasTires' + (isModal ? '_modal' : '')).prop('checked'),
        hasFood: $('#locator_hasFood' + (isModal ? '_modal' : '')).prop('checked'),
        hasHearing: $('#locator_hasHearing' + (isModal ? '_modal' : '')).prop('checked'),
        hasOptical: $('#locator_hasOptical' + (isModal ? '_modal' : '')).prop('checked'),
        hasPharmacy: $('#locator_hasPharmacy' + (isModal ? '_modal' : '')).prop('checked'),
        hasBusiness: $('#locator_hasBusiness' + (isModal ? '_modal' : '')).prop('checked'),
        hasPhotoCenter: $('#locator_hasPhotoCenter' + (isModal ? '_modal' : '')).prop('checked'),
        hasCarWash: $('#locator_hasCarWash' + (isModal ? '_modal' : '')).prop('checked'),
      };
    },
    /**
     * Dynamically sets the height/width of the BING map containers
     * @return {void}
     */
    format_map_container: function() {
      let $bingMap = $('#bing-map');
      let $mobileBingMap = $(Locator.is_details() ? '#bing-map' : '#mobile-bing-map');
      $bingMap.width($('#map-container').width());
      $mobileBingMap.width($('#locator').width());
      $("#bing-map").css("width", "inherit"); 
      // Hiding the container because it has a border that we don't always want ot display
      $('#mobile-map-container, #map-container').hide();
      if ($(window).width() < 769) {
        $('#mobile-map-container').show();
        $mobileBingMap.height($(window).height() / 3);
        if (Locator.is_details()) {
          $('#map-container').show();
        }
      } else {
        $('#map-container').show();
        $bingMap.height(500);
      }
    },
    /**
     * Creates an instance of the BING map and stores it in the Locator object.
     * @param  {string} mapId the BING map container id
     * @return {void}
     */
    init_bing_map: function(mapId) {
    	
      var globalBingStaticKeyVar = $('#globalBingStaticKey').val();
    	
      if (typeof Locator.get_map() === 'undefined') {
				mapId = mapId || this.get_map_id();
        Locator.set_map(new Microsoft.Maps.Map('#' + mapId, {
          credentials: globalBingStaticKeyVar,
          mapTypeId: Microsoft.Maps.MapTypeId.road,
          enableHighDpi: true,
          liteMode: true,
          navigationBarMode: Microsoft.Maps.NavigationBarMode.minified,
        }));
      }
    },
    /**
     * Creates an instance of the MS module "AutoSuggest" and stores the manager
     * to the Locator object
     * @return {void}
     */
    init_auto_suggest: function() {
      if (!Locator.auto_suggest_manager && $(window).width() > 768) {
        Microsoft.Maps.loadModule('Microsoft.Maps.AutoSuggest', function() {
          let options = {
            maxResults: 4,
            map: Locator.get_map(),
          };
          Locator.auto_suggest_manager = new Microsoft.Maps.AutosuggestManager(options);
          Locator.auto_suggest_manager.attachAutosuggest('#search-warehouse', '#search-warehouse-container',
            function(suggestionResult) {
              $('#search-warehouse').val(suggestionResult.formattedSuggestion);
              Locator.search();
            });
        });
      } else if (Locator.auto_suggest_manager && $(window).width() < 769) {
        Locator.auto_suggest_manager.detachAutosuggest();
        Locator.auto_suggest_manager = undefined;
      }
    },
    /**
     * Initializes a BING map object and sets it to the Locator object
     * Locator.map. The map is placed in the map_id container. Instantiates
     * the auto suggest manager then performs a search.
     * @param  {string} mapId the BING map container id
     * @return {void}
     */
    locations_perform_execute: function(mapId) {
      Locator.init_bing_map(mapId);
      //Locator.init_auto_suggest();
      // Locator.init_on_mouseup();
      Locator.search();
    },
    /**
     * Bind a listener on the BING map that progressively aggregates more warehouses
     * to the map and list when the user releases their mouse click. Uses the lat/long
     * from the center of the map to get more results.
     * @return {void}
     */
    init_on_mouseup: function() {
      Microsoft.Maps.Events.addHandler(Locator.get_map(), 'mouseup', function() {
        let location = Locator.get_map().getCenter();
        $.get('/AjaxWarehouseBrowseLookupView?' + Locator.get_querystrings(location))
          .done(function(data) {
            try {
              // parse the data string from ajax call
              let dataAsJson = JSON.parse(data);
              // the first object is not a 'warehouse' object.
              // this is a boolean flag indicating if there are more results
              Locator.hasMoreWarehouses = dataAsJson[0]; 
              // after setting the flag, remove the first object and let the processing continue
              dataAsJson.splice(0,1);
              let exisitingWarehouses = Locator.warehouses.map(function(warehouse) {
                return warehouse.identifier;
              });
              let newWarehouses = dataAsJson.filter(function(warehouse) {
                return exisitingWarehouses.indexOf(warehouse.identifier) == -1;
              });
              Locator.warehouses = Locator.warehouses.concat(newWarehouses);
              Locator.build_warehouse_list(true);
              if (newWarehouses.length > 0) {
                Locator.remove_pins(Locator.get_map());
                Locator.add_pins(true);
              }
              Locator.add_viewmore_button();
            } catch (e) {
              console.error(e);
            }
          });
      });
    },
    /**
     * Initializes a BING map object and sets the map appropriately on the details page.
     * @param  {string} mapId the BING map container id
     * @return {void}
     */
    details_perform_execute: function(mapId) {
      let warehouseNumber = $('#warehouse').data('identifier').toString();
      Locator.init_bing_map(mapId);
      Locator.get_warehouses(warehouseNumber);
      Locator.details_set_links();
    },
    details_set_links: function() {
      // Give the a.phones a callable link for mobile views
      $('a.phone').each(function(i, phone) {
        $(phone).attr('href', 'tel:' + Locator.get_callable_link($(phone).text()));
      });
    },
    /**
     * Gets the location using a priority system.
     * @return {object} The promise to be resolved based on what is set
     */
    get_location: function() {
      let deferred = $.Deferred();
      let locationGeo = "";
      // This tells the application to first use the search term set by the query string,
      // cookie, or geolocation on page load. Any subsequent searches use the input field value.
      // CABD akamai sniffer not enabled  
      if (Locator.isFirstLoad) {
        if($.cookie('txtLocation') == null){
        	if ($('#isBDAppVar').val() == 'BD'){
        		var wcsZipSet = $.cookie('WC_BD_ZIP')
        		if(typeof wcsZipSet === "undefined") {
        			var akamiSniffZip = $.cookie('client-zip-short')
        			if(typeof wcsZipSet !== "undefined") {
        				$.cookie('txtLocation', $.cookie('client-zip-short'), {domain: wcs.domain});        			
        			}
        		}else{
        			$.cookie('txtLocation', $.cookie('WC_BD_ZIP'), {domain: wcs.domain});        			
        		}
        	}else{
        		$.cookie('txtLocation', $.cookie('invCheckPostalCode'), {domain: wcs.domain});
        	}
        }
    	  let Geo = $('#search-warehouse').val() // Priority 1: search
	         || COSTCO.util.getParameterByName('location') // Priority 2: query string
	         || $.cookie('txtLocation')// Priority 3: cookie
	         || ""; // Fallback: assign empty string to avoid NPE
	        Locator.isFirstLoad = false;
	        const space = Geo.split(" ");
	        	for (let i = 0; i < space.length; i++) {
	        	space[i] = space[i].charAt(0).toUpperCase() + space[i].slice(1); //Changing first letter to uppercase  
	        	}
	        locationGeo = space.join(" ");
      } else {
        locationGeo = $('#search-warehouse').val();
      }
      if (Locator.isFirstLoad && (locationGeo === null || locationGeo === '') && navigator && navigator.geolocation) { // Priority 4: Gelocation
        navigator.geolocation.getCurrentPosition(function(position) {
          deferred.resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        }, function(error) {
          console.error('Geolocation: ' + error.message);
          return deferred.resolve('');
        });
      } else {
        return deferred.resolve(locationGeo.trim());
      }
      return deferred.promise();
    },
    /**
     * Sets the lat_long object with lat/long and then gets the warehouses
     * to be displayed using the lat/long.
     * @param  {number} latitude  Latitude as a number
     * @param  {number} longitude Longitude as a number
     * @return {void}
     */
    search_by_geolocation: function(latitude, longitude) {
      Locator.lat_long.latitude = latitude;
      Locator.lat_long.longitude = longitude;
      Locator.get_warehouses({
        latitude: latitude,
        longitude: longitude,
      });
    },
    /**
     * onclick handler for the "Get Directions" link. Opens a new tab and redirects
     * to maps.google.com using Current Location as starting addr and warehouse
     * address as the destination address.
     * @param  {number} index used to get the warehouse (not 0 indexed)
     * @return {void}
     */
    get_directions: function(index) {
      let warehouse = COSTCO.Locator.warehouses[index - 1];
      let warehouseAddress = encodeURIComponent(
        ['Costco'].concat(warehouse.address1.split(' '))
        .concat(warehouse.city.split(' '))
        .concat(warehouse.state.split(' '))
        .concat(warehouse.country.split(' '))
        .join(' '));
      search = $('#search-warehouse').val();
      let win = window.open(
        window.location.protocol + '//maps.google.com?saddr=Current+Location&daddr=' + warehouseAddress, '_blank');
      win.focus();
    },
    /**
     * Calling this function represents a new search and thus some state
     * is cleared. The Locator warehouses are cleared and the cookie with the new
     * location is set. If the location is < 5 digits and is numeric, we're looking
     * for a warehouse number otherwise we're lazy loading the MS search module
     * and geocoding the location so that we can request nearby warehouses from WAS
     * using the lat/long of the results.
     * @return {void}
     */
    search: function() {
      Locator.get_location().done(function(location) {
        if (!location) {
          // If get_location returns an empty string set view to united states
          console.error('Couldn\'t resolve location: "' + location + '", setting default view.');
          Locator.error_view(true);
        } else if (typeof location === 'object' && location.latitude && location.longitude) {
          // If get_location returns lat/long
          Locator.search_by_geolocation(location.latitude, location.longitude);
        } else {
          // If get_location returns a location string.
          $('#search-warehouse').val(location);
          // Set location in cookie and set domain to expose cookie to subdomains
          $.cookie('txtLocation', location, {domain: wcs.domain});
          // Its a warehouse number search
          if (!isNaN(parseInt(location)) && location.length < 5) {
            Locator.get_warehouses(location);
            return;
          }
          Locator.geocode_location(Locator.get_map(), location);
        }
      });
    },
    /**
     * Tires variation functionality. This function is the onclick handler for the
     * select buttons on each warehouse. Its purpose is to call the SetWarehouseNumberCmd
     * with the appropriate warehouse number and then redirect using the response redirecturl.
     * @param  {number} index used to get the warehouse object (from warehouse list)
     * @return {void}
     */
    set_tire_warehouse: function(index) {
      let warehouse = COSTCO.Locator.warehouses[index - 1]; // not 0 index
      let orderitemId = COSTCO.util.getParameterByName('orderitemId');
      let params = {
        requesttype: 'ajax',
        warehouseNumber: warehouse.identifier,
        shipToWarehouse: warehouse.isShipToWarehouse,
        fromPage: COSTCO.util.getParameterByName('fromPage') ?
          encodeURIComponent(COSTCO.util.getParameterByName('fromPage')) : 'WarehouseTiresLocatorView',
      };
      $('[data-warehouse=' + warehouse.identifier + '] .tires-warehouse-select button')
        .attr('disabled', 'disabled');
      // From checkout flow needs orderitemId
      if (orderitemId) {
        params.orderitemId = encodeURIComponent(orderitemId);
      }
      $.ajax({
          url: '/SetWarehouseNumberCmd?' + $.param(params),
          type: 'POST',
        })
        .done(function(data, textStatus, jqXHR) {
          data = COSTCO.util.scrubSpecialChars(data);
          try {
            data = JSON.parse(data);
            window.location.href = data.redirecturl.replace(/&amp;/g, '&');
          } catch (e) {
            console.error(e);
          }
        })
        .fail(function(jqXHR, textStatus, errorThrown) {
          console.error(errorThrown);
        })
        .always(function(data, textStatus, errorThrown) {
        	data = COSTCO.util.scrubSpecialChars(data);
          try {
            data = JSON.parse(data);
            $('[data-warehouse=' + data.warehouseNumber[0] + '] .tires-warehouse-select button')
              .removeAttr('disabled');
          } catch (e) {
            console.error(e);
          }
        });
    },
    /**
     * [description]
     * @param  {[type]} index [description]
     * @return {void}
     */
    set_pickup_warehouse: function(index) {
      let pickUpItemWarehouseLocator = $('#pickUpItemWarehouseLocator').val();
      if ( pickUpItemWarehouseLocator == 'true' ){
    	  COSTCO.Locator.set_My_warehouse(index);
      }
      let warehouse = COSTCO.Locator.warehouses[index - 1]; // not 0 index
      let orderItemId = COSTCO.util.getParameterByName('orderitemId');
      let shipModeId = COSTCO.util.getParameterByName('shipModeId');
      let params = {
        requesttype: 'ajax',
        pickupWarehouseNum: warehouse.identifier,
        fromPage: COSTCO.util.getParameterByName('fromPage') ?
          encodeURIComponent(COSTCO.util.getParameterByName('fromPage')) : 'CheckoutCartView',
        showMultiAddressSelectPage: (COSTCO.util.getParameterByName('multiShipPage')),
        checkoutPage: COSTCO.util.getParameterByName('checkoutPage'),
      };
      if (orderItemId) {
        params.orderItemId = encodeURIComponent(orderItemId);
      }
      if (shipModeId) {
        params.shipModeId = encodeURIComponent(shipModeId);
      }
      $('[data-warehouse=' + warehouse.identifier + '] .pickup-warehouse-select button').attr('disabled', 'disabled');
      $.ajax({
          url: '/CostcoOrderWarehousePickupUpdateCmd?' + $.param(params),
          type: 'POST',
        })
        .done(function(data, textStatus, jqXHR) {
          try {
            data = JSON.parse(DOMPurify.sanitize(data));
            window.location.href = data.redirecturl.replace(/&amp;/g, '&');
          } catch (e) {
            console.error(e);
          }
        })
        .fail(function(jqXHR, textStatus, errorThrown) {
          console.error(errorThrown);
        })
        .always(function(data, textStatus, errorThrown) {
          try {
            data = JSON.parse(data);
            $('[data-warehouse=' + data.warehouseNumber[0] + '] .pickup-warehouse-select button')
              .removeAttr('disabled');
          } catch (e) {
            console.error(e);
          }
        });
    },
    
    /**
     * Method to update new pickup warehouse.
     * @param  {string} warehouse - Updated warehouse.
     */
    updatePickUpWarehouse: function(warehouse) {
    	try {
    		$.ajax({
	    		url: '/AjaxWarehouseUpdateCmd?warehouseDrawer=FMW&warehouseId='+warehouse,
	            type: 'POST',
	    	})
	        .done(function(data, textStatus, jqXHR) {})
	        .fail(function(jqXHR, textStatus, errorThrown) {
	        	console.error(errorThrown);
	        })
	        .always(function(data, textStatus, errorThrown) {});
    	} catch (e) {
    		console.error(e);
    	}
    },
    
    /**
     * Makes the geocode request to BING. A success means we're going to get warehouses
     * using those results. If nothing is returned we're showing an error view.
     * @param  {object} map      The Microsoft.Map defined on Locator.map
     * @param  {string} location the query representing the location we're looking for.
     * @return {void}
     */
    geocode_location: function(map, location) {
      let searchRequest = {
        where: location,
        callback: Locator.get_warehouses,
        errorCallback: function(e) {
          // If there is an error, alert the user about it.
          console.error('BING Couldn\'t find location ' + JSON.stringify(e));
          if (Locator.error_count < 1) {
            // We'll retry this automatically once to see if there was an
            // anomoly. A successful call to BING resets this counter.
            Locator.error_count++;
            Locator.search();
          } else {
            Locator.error_view(true);
          }
        },
        includeCountryIso2: true, // return 2-Letter ISO Code of Country for the location. Will be used to filter on STLOC.COUNTRY
      };
      // Make the geocode request.
      Locator.get_search_manager().done((searchManager) => {
        searchManager.geocode(searchRequest);
      });
    },
    /**
     * We have two separate managers for each map, "mobile" and "desktop". This
     * function returns the appropriate one. Beceause lazy loading the
     * Microsoft search manager requires an async call, we use a deferred
     * object to resolve when we get the appropriate mana
     * @return {object} The deferred object to be resolved.
     */
    get_search_manager: function() {
      let deferred = $.Deferred();
      let mapId = Locator.get_map_id();
      if (mapId == 'bing-map' && typeof Locator.search_manager.desktop === 'undefined') {
        Microsoft.Maps.loadModule('Microsoft.Maps.Search', function() {
          Locator.search_manager.desktop = new Microsoft.Maps.Search.SearchManager(Locator.get_map());
          // Locator.geocode_location(Locator.get_map(), location);
          return deferred.resolve(Locator.search_manager.desktop);
        });
      } else if (mapId == 'mobile-bing-map' && typeof Locator.search_manager.mobile === 'undefined') {
        Microsoft.Maps.loadModule('Microsoft.Maps.Search', function() {
          Locator.search_manager.mobile = new Microsoft.Maps.Search.SearchManager(Locator.get_map());
          // Locator.geocode_location(Locator.get_map(), location);
          return deferred.resolve(Locator.search_manager.mobile);
        });
      } else {
        return mapId == 'bing-map'
          ? deferred.resolve(Locator.search_manager.desktop)
          : deferred.resolve(Locator.search_manager.mobile);
      }
      return deferred.promise();
    },
    /**
     * Takes the array of objects from bing, the lat_long object, or the warhouse number
     * string and ajax gets the warehouses from WAS then calls configure_map for another sequence
     * of actions.
     * @param  {array|string} r The array of objects with lat/long from bing
     *                          or the string representing a warehouse number
     * @return {void}
     */
    get_warehouses: function(r) {
      if (r && r.results && r.results.length > 0) { // list of locations from BING
        Locator.error_count = 0; // Successful call to BING resets this.
        // For each location returned from bing maps, return the warehouses
        // surrounding that location.
        //
        // We're only getting the first geocoded result from bing to
        // pass to ajax_warehouses. This is strange but sequential calls to
        // /AjaxWarehouseBrowseLookupView (point of using $.when) sometimes
        // don't return data. Its like the server doesn't like calls that close together
        let results = Locator.get_localized_result(r.results);
        $.when(...Locator.ajax_warehouses(results))
          .done(Locator.configure_map);
      } else if (Locator.lat_long.latitude && Locator.lat_long.longitude) {
        // Geolocation for nearby warehouses. Set the latitude and longitude in the query strings.
        $.when(Locator.ajax(Locator.get_querystrings({
          latitude: Locator.lat_long.latitude,
          longitude: Locator.lat_long.longitude,
        }))).done(Locator.configure_map);
      } else if (!isNaN(parseInt(r)) && r.length < 5) { // warehouse number
        $.when(Locator.ajax(Locator.get_querystrings(r)))
          .done(Locator.configure_map);
      } else {
        // can't help you sucka
      }
    },
    /**
     * Using the wcs.langId, this function returns the localized object inside of
     * an array. It filters out all other results except for the first match.
     * @param  {array} results The array of objects representing matches from BING
     * @return {array}         The array containing one object to return
     */
    get_localized_result: function(results) {
      let result = [];
      let countryRegion = wcs.langId == '-1' ? 'United States' : 'Canada';
      try {
        for (let i = 0, len = results.length; i < len; i++) {
          let location = results[i];
          if (location.address.countryRegion == countryRegion) { // prefer the US result
            result.push(location);
            break;
          }
          if (i == results.length - 1 && result.length == 0) { // Nothing matching region? Use first location from bing
            result.push(results[0]);
          }
        }
      } catch (e) {
        console.error(e);
        return [results[0]];
      }
      return result;
    },
    /**
     * The onclick handler for each .warehouse in the warehouse list. When a warehouse
     * in the list is clicked, the event handler of the pin on the map is clicked.
     * @param  {number} pindex the index to use to fetch the corresponding pin
     * @return {void}
     */
    select_pin: function(pindex) {
      let pushpin = COSTCO.Locator.get_map().entities.get(pindex - 1);
      let data = [];
      data.target = pushpin;
      Microsoft.Maps.Events.invoke(pushpin, 'click', data);
    },
    /**
     * Invoking this function clears some state then adds pins,
     * sets the viewing boundary and builds the warehouse list of we have
     * at least one warehouse in memory, otherwise we throw an error view.
     * @return {void}
     */
    configure_map: function() {
      // Clear error state
      Locator.error_view(false);
      // Clear map pin state.
      Locator.remove_pins(Locator.get_map());
      // Clear infobox
      if (Locator.infobox) {
        Locator.infobox.setMap(null);
      }
      if (Locator.warehouses.length > 0) {
        Locator.add_pins();
        Locator.set_view();
        if (!Locator.is_details()) {
          Locator.build_warehouse_list();
          Locator.add_viewmore_button();
        }
      } else {
        Locator.error_view(true);
      }
    },
    /**
     * Sets the BING map in the Locator object based on the context of the caller.
     * @param  {Microsoft.Maps.Map} map The BING map to set
     * @return {void}
     */
    set_map: function(map) {
      let mapId = this.get_map_id();
      if (mapId == 'mobile-bing-map'
        && typeof Locator.map.mobile === 'undefined'
        && !Locator.is_details()) { // Warehouse details only uses the desktop map
        this.map.mobile = map;
      } else if (mapId == 'bing-map'
        && typeof Locator.map.desktop === 'undefined'
        || Locator.is_details()) { // Warehouse details only uses the desktop map
        this.map.desktop = map;
      }
    },
    /**
     * Getter function to retrieve the BING map according to the context of the
     * caller. Additionally, if the user arrived from Warehouse Details page,
     * return the desktop map because we only use this map here.
     * @return {Microsoft.Maps.Map} The BING map object
     */
    get_map: function() {
      return this.get_map_id() == 'bing-map' || Locator.is_details()
        ? Locator.map.desktop
        : Locator.map.mobile;
    },
    /**
     * Clears the warehouse list and pins from the map and then focuses on the good
     * ol' united states of america.
     * @param  {Boolean} isDisplay the boolean flag to show/not show an error message
     * @return {void}
     */
    error_view: function(isDisplay) {
      $('#warehouse-list').children().remove();
      $('#error-container').children().remove();
      Locator.remove_pins(Locator.get_map());
      let usa = new Microsoft.Maps.Location(37.0902, -95.7129);
      Locator.get_map().setView({
        center: usa,
        zoom: 3,
      });
      if (Locator.infobox) {
        Locator.infobox.setMap(null);
      }
      if (isDisplay) {
        $('#error-container')
          .append(COSTCO.util.tmpl('tmpl_locator_error', {}));
      }
    },
    /**
     * [description]
     * @param  {String} querystrings the url encoded query strings going to WAS
     * @return {Object}              Returns an $.deferred ajax call whose
     *                                       purpose is to fetch nearby warehouses
     *                                       using @param querystrings.
     */
    ajax: function(querystrings) {
      return $.ajax({
        url: '/AjaxWarehouseBrowseLookupView?' + querystrings,
        timeout: 10000,
        cache: true,
      }).done(function(warehouseStr) {
        try {
          // Clear warehouses
          let warehouseStrJson = JSON.parse(warehouseStr);
          Locator.hasMoreWarehouses = warehouseStrJson[0];
          warehouseStrJson.splice(0,1);
          Locator.warehouses = [];
          Locator.warehouses = Locator.warehouses
            .concat(warehouseStrJson);
        } catch (e) {
          console.error(e);
        }
      }).fail(function(jqXHR, textStatus, errorThrown) {
        console.error(errorThrown);
      });
    },
    /**
     * Takes the array of objects with lat/long (from BING) and builds an array
     * of deferred objects (each object represents an ajax call to WAS) to be passed
     * to a $.when. "When" its done, it'll set the map.
     * @param  {array} results The array of BING locations
     * @return {object}        The aggregated array of deferred objects.
     */
    ajax_warehouses: function(results) {
      let deferreds = [];
      (results || []).forEach(function(resultObj) {
        (resultObj.locations || []).forEach(function(location) {
        	if(resultObj.address && resultObj.address.countryRegionISO2){
        		location["countryCode"] = resultObj.address.countryRegionISO2; //If country code is present, add to location object
        	}
          let querystrings = Locator.get_querystrings(location);
          deferreds.push(Locator.ajax(querystrings));
        });
      });
      return deferreds;
    },
    /**
     * Gets the URL encoded string built from the params object.
     * @param  {object|string} location Either a passed object with lat/long or
     *                                  the warehouse number.
     * @return {void}
     */
    get_querystrings: function(location) {
      let filterState = Locator.get_filter_state();
      let params = {
        langId: wcs.langId || '-1',
        storeId: wcs.storeId || '10301',
        numOfWarehouses: 10 + Locator.additionalWarehouses, // increment result count with every click of 'view more' button
        hasGas: filterState.hasGas,
        hasTires: Locator.is_tires ?
          true : filterState.hasTires,
        hasFood: filterState.hasFood,
        hasHearing: filterState.hasHearing,
        hasPharmacy: Locator.is_transfer_warehouse ?
          true : filterState.hasPharmacy,
        hasOptical: filterState.hasOptical,
        hasBusiness: filterState.hasBusiness,
        hasPhotoCenter: filterState.hasPhotoCenter,
        tiresCheckout: Locator.is_tires ? 1 : 0,
        hasCarWash: filterState.hasCarWash,
        isTransferWarehouse: Locator.is_transfer_warehouse ?
          true : false,
        populateWarehouseDetails: true,
        warehousePickupCheckout: Locator.is_pickup_warehouse ? true : false,
      };
      if (location.latitude && location.longitude) {
        params.latitude = location.latitude;
        params.longitude = location.longitude;
      } else {
        params.warehouseNumber = location;
      }
      params.countryCode = location.countryCode;
      return $.param(params);
    },
    
    /**
     * Check if a specific cookie with the given name exists    
     * @return {boolean}
     */
    cookieExists: function(name) {
        name = encodeURIComponent(name);
        console.log(name);
        const regexp = new RegExp(`(?:^${name}|;\\s*${name})=(.*?)(?:;|$)`, 'g');
        const exists = regexp.test(document.cookie);
        console.log(exists);
        return exists;
      },

      /**
       * Retrieves value of cookie for given input name.
       * @param {string} name name for the "cookie".
       * @returns {any} stored value/object(serialized), null if cookie does not exists
       */
      get_cookie: function(name) {
        let cookieValue = null;
        console.log(name)
        if (Locator.cookieExists(name)) {
          const regexp = new RegExp(`(?:^${encodeURIComponent(name)}|;\\s*${encodeURIComponent(name)})=(.*?)(?:;|$)`, 'g');
          const result = regexp.exec(document.cookie);
          cookieValue = decodeURIComponent(result[1]);
          try {
            cookieValue = JSON.parse(cookieValue);
          } catch (e) {
            // cookieValue = cookieValue; as part of sonar fix
          }
        }
        return cookieValue;
      },
      
      /**
       * Builds cookie string includes cookievalue,domain and expiry time    
       * @return {var}
       */
      buildCookieString: function(name, value, days) {
  	    let expires;
  	    // let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)};path=${window.hostname}`; // Removed path as its not working in IE.
  	    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)};path=/`;
  	    if (days) {
  	      var date = new Date();
  	      date.setTime(date.getTime() + (days*24*60*60*1000));
  	      expires = "; expires=" + date.toUTCString();
  	      cookieString = cookieString + expires;
  	    }     
  	    return cookieString;
  	 },
  	
  	 /**
     * Order the json value of the cookie based on ascending order    
     * @return {var}
     */
  	 orderCookie: function(cookie) {
  	    const ordered = Object.keys(cookie).sort().reduce(
  	      (obj, key) => {
  	      // eslint-disable-next-line no-param-reassign
  	        obj[key] = cookie[key];
  	        return obj;
  	      },
  	      {}
  	    );
  	    return ordered;
  	  },
    
  	 /**
       * Sets the value of a cookie    
       * @return {var}
       */
     set_cookie: function(name, value, days) {
    	    this.cookie = Locator.buildCookieString(name, value, days);    
    	    document.cookie =  this.cookie;
     },
      
    /**
     * This function handles building the warehouse list that corresponds
     * with the pins on the BING map.
     * @param {Boolean} isNewWarehouse - Optional switch. If true, tells this
     * 	function to not resize the map as we're just dynamically adding pins.
     */
    /**
     * This function handles building the warehouse list that corresponds
     * with the pins on the BING map.
     * @param  {Boolean} isNewWarehouse Optional switch. If true, tells this
     *                                  function to not resize the map as we're
     *                                  just dynamically adding pins.
     * @return {void}
     */
    build_warehouse_list: function(isNewWarehouse) {
      $('#warehouse-list').children().remove();
      let $warehouseList = $('#warehouse-list');
      (Locator.warehouses || []).forEach(function(warehouse, i) {
    	let isOpenDateFuture = new Date(warehouse.openDate) > new Date();
    	let whSelected = COSTCO.util.getParameterByName('whSelected');
        $warehouseList.append(COSTCO.util.tmpl('tmpl_warehouse', {
          is_my_warehouse: Locator.get_current_warehouse(warehouse),
          pickUpItemWarehouseLocator: $('#pickUpItemWarehouseLocator').val(),
          is_tires: Locator.is_tires,
          is_transfer_warehouse: Locator.is_transfer_warehouse,
          is_pickup_warehouse: Locator.is_pickup_warehouse,
          pin: COSTCO.util.tmpl('tmpl_locator_pin', {
            number: i + 1,
            weight: 400,
            locationName: warehouse.locationName,
          }),
          index: i + 1,
          identifier: warehouse.identifier,
          country: warehouse.country,
          locationName: warehouse.locationName,
          address1: warehouse.address1,
          city: warehouse.city,
          state: warehouse.state,
          zipCode: warehouse.zipCode,
          isOpenDateFuture: isOpenDateFuture,
          whSelected: whSelected,
          phone: warehouse.phone,
          regionCode: warehouse.regionCode,
          callable_link: Locator.get_callable_link(warehouse.phone),
          distance: warehouse.distance.toFixed(2),
          measure: wcs.langId == '-1' ? 'mi' : 'km',
          hasGas: warehouse.hasGasDepartment,
          hasBusiness: warehouse.hasBusinessDepartment,
          hasPhotoCenter: warehouse.hasPhotoCenterDepartment,
          hasPharmacy: warehouse.hasPharmacyDepartment,
          hasOptical: warehouse.hasOpticalDepartment,
          hasHearing: warehouse.hasHearingDepartment,
          hasFood: warehouse.hasFoodDepartment,
          hasTires: warehouse.hasTiresDepartment,
          hasCarWash: warehouse.hasCarWash,
          tiresPhone: warehouse.tiresDepartmentPhone || '',
          pharmacyPhone: Locator.get_service_phone('Pharmacy', warehouse),
          isShipToWarehouse: warehouse.isShipToWarehouse,
          isEnableShipToHome: warehouse.enableShipToHome,
          tires_callable_link: Locator.get_callable_link(warehouse.tiresDepartmentPhone),
          pharmacy_callable_link: Locator.get_callable_link(
            Locator.get_service_phone('Pharmacy', warehouse)),
          location_href: Locator.get_location_href(warehouse),
        }));
      });
      if (!isNewWarehouse) {
        Locator.resize_elements();
        // Scroll to top of list
        $('#warehouse-list-container')
          .scrollTo('.warehouse[data-num=1]', 100);
      }
      // for tires locator, need to stop event propagation when user clicks on get directions or store details
      // since a click anywhere on the row takes you to tires site.
      if (Locator.is_tires) {
        $('#warehouse-list-container').find('a:not(.warehouse-name)').on('click', function(e) {
          e.stopPropagation();
        });
      }
    },
    
    /**
     * This function opens and closes accordion that contains warehouse details
     * @param  {String} stateCode used to filter list of all warehouses
     * @param  {String} html element that was clicked 
     * @return {void}
     */
    show_warehouses_by_state(stateCode, element) {
    	if($('#warehouse-by-state-accordion-collapse').hasClass('in') && $('#warehouse-by-state-accordion-collapse').attr('data-state') == stateCode) {
			$('#warehouse-by-state-accordion-collapse').collapse('hide');
			$(element).children(".warehouse-close").addClass("hidden");
			$(element).removeClass("warehouse-title-active");
			$('html, body').animate({scrollTop: $(element).offset().top}, 800);
		} else{
			
			if($('#warehouse-by-state-accordion-collapse').hasClass('in')) {
				
				$('#warehouse-by-state-accordion-collapse').collapse('hide');

				setTimeout(function(){ 
					Locator.build_warehouses_by_state(stateCode, element);
				}, 700);
			} else {
				Locator.build_warehouses_by_state(stateCode, element);
			}	
		}
    },
    
    /**
     * This function handles building the warehouse list that corresponds
     * with Warehouse By State page.
     * @param  {String} stateCode used to filter list of all warehouses
     * @param  {String} html element that was clicked 
     * @return {void}
     */
    build_warehouses_by_state(stateCode, element) {
		
		$('#warehouse-by-state-accordion-collapse').remove();
		
		$('html, body').animate({scrollTop: $(element).offset().top}, 800);
		
		var warehousesByState = allWarehousesList.find(function(el){
			return el.stateCode == stateCode;
		});
		
		$(".panel-group div div div").removeClass("warehouse-title-active");
		$(".panel-group div div .warehouse-close").addClass("hidden");
		$(element).addClass("warehouse-title-active");
		$(element).children(".warehouse-close").removeClass("hidden");
		
		$(element).parent().after(COSTCO.util.tmpl('tmpl_warehouse_by_state', {
    		state: stateCode
    	}));
    	
    	$('#warehouse-by-state-accordion-collapse-panel').children().remove();
        (warehousesByState.warehouseList || []).forEach(function(warehouse, i) {
        	$('#warehouse-by-state-accordion-collapse-panel').append(COSTCO.util.tmpl('tmpl_warehouse_by_state_items', {
            is_tires: Locator.is_tires,
            is_transfer_warehouse: Locator.is_transfer_warehouse,
            is_pickup_warehouse: Locator.is_pickup_warehouse,
            is_my_warehouse: Locator.get_current_warehouse(warehouse),
            pin: COSTCO.util.tmpl('tmpl_warehouse_locator_pin', {
              number: i + 1,
              weight: 400,
              locationName: warehouse.locationName,
            }),
            index: i + 1,
            identifier: warehouse.identifier,
            country: warehouse.country,
            locationName: warehouse.locationName,
            address1: warehouse.address1,
            city: warehouse.city,
            state: warehouse.state,
            zipCode: warehouse.zipCode,
            phone: warehouse.phone,
            callable_link: Locator.get_callable_link(warehouse.phone),
            fullAddressEncoded: warehouse.fullAddressEncoded,
            distance: warehouse.distance.toFixed(2),
            measure: wcs.langId == '-1' ? 'mi' : 'km',
            hasGas: warehouse.hasGasDepartment,
            hasBusiness: warehouse.hasBusinessDepartment,
            hasPhotoCenter: warehouse.hasPhotoCenterDepartment,
            hasPharmacy: warehouse.hasPharmacyDepartment,
            hasOptical: warehouse.hasOpticalDepartment,
            hasHearing: warehouse.hasHearingDepartment,
            hasFood: warehouse.hasFoodDepartment,
            hasTires: warehouse.hasTiresDepartment,
            hasCarWash: warehouse.hasCarWash,
            tiresPhone: warehouse.tiresDepartmentPhone || '',
            pharmacyPhone: Locator.get_service_phone('Pharmacy', warehouse),
            isShipToWarehouse: warehouse.isShipToWarehouse,
            isEnableShipToHome: warehouse.enableShipToHome,
            tires_callable_link: Locator.get_callable_link(warehouse.tiresDepartmentPhone),
            pharmacy_callable_link: Locator.get_callable_link(
            	Locator.get_service_phone('Pharmacy', warehouse)),
            location_href: Locator.get_location_href(warehouse),
          }));
        });
		
		$('#warehouse-by-state-accordion-collapse').collapse('show');
	},
    
	/**
     * Opens google maps for provided location in new tab
     * @param  {String} newLocation opens google maps for this location
     * @return {void}
     */
    open_location(newLocation) {
    	let win = window.open(
    	window.location.protocol + '//maps.google.com?saddr=Current+Location&daddr=' + newLocation, '_blank');
    	win.focus();
    },
    
    /**
     * Helper method to retrieve the phone number from a designated
     * core service.
     * @param  {string} service   The service we're looking for
     * @param  {object} warehouse The object to get the service phone from
     * @return {string}           The service phone number matching the
     *                                "service" of the warehouse
     */
    get_service_phone: function(service, warehouse) {
      let services = warehouse.coreServices || [];
      for (let i = services.length - 1; i >= 0; i--) {
        if (service == services[i].name) {
          return services[i].phone || '';
        }
      }
      return '';
    },
    /**
     * Helper method for producing a "callable" link by stripping extraneous
     * characters from a phone number string. This link will be callable from a
     * mobile device.
     * @param  {string} phoneNumber The unformatted phone number
     * @return {string}             The callable phone number
     */
    get_callable_link: function(phoneNumber) {
      return phoneNumber
        .split(' ').join('') // Remove spaces
        .split('(').join('') // Remove (
        .split(')').join('') // Remove )
        .split('-').join(''); // Remove -
    },
    /**
     * Sets the height and width dimensions of the bing container and the
     * warehouse list.
     * @return {void}
     */
    resize_elements: function() {
      if ($(window).width() >= 769) { // only for higher resolutions
        $('#warehouse-list-container').height(600);
        let listHeight = $('#list-column').height();
        listHeight > 400 ?
          $('#bing-map').height(listHeight) :
          $('#bing-map').height(400);
      } else {
        $('#warehouse-list-container').css({
          height: 'auto',
        });
      }
      // To address the occasional blurry rendering of the map.
      // Hate this hack but its necessary.
      let map = $(Locator.get_map().getRootElement());
      map.height(map.height() - 1);
      setTimeout(function() {
        map.height(map.height() + 1);
      }, 1000);
    },
    /**
     * The onclick handler of the 'X' on the infobox (on map). Closes the box, obviously.
     * @return {void}
     */
    close_infobox: function() {
      Locator.infobox.setOptions({
        visible: false,
      });
    },
    /**
     * Gets the warehouse location href to be applied to "Store Details" and the
     * location name inside the infobox.
     * @param  {object} warehouse The warehouse object used to build the href
     * @return {string}           The warehouse location href
     */
    get_location_href: function(warehouse) {
      let warehouseName = '';
      if (warehouse.locationName.toLowerCase() == warehouse.city.toLowerCase()) {
        warehouseName = warehouse.locationName.toLowerCase() + '-' + warehouse.state.toLowerCase();
      } else {
        warehouseName = warehouse.locationName.toLowerCase() + '-' + warehouse.city.toLowerCase() + '-' + warehouse.state.toLowerCase();
      }
      return '/warehouse-locations/'
        .concat(warehouseName.replace(/[^\w\d]/gi, '-').replace(/\-{2,}/, '-'))
        .concat('-' + warehouse.identifier + '.html');
    },
    /**
     * Gets the warehouse location href to be applied to "Store Details" and the
     * location name inside the infobox.
     * @param  {object} warehouse The warehouse object used to build the href
     * @return {string}           The warehouse location href
     */
    get_current_warehouse: function(warehouse) {    	 
      let warehouseId = '';
      var currentWarehouse;
      if (Locator.cookieExists(cookies.WAREHOUSE_COOKIE)){
    	  var warehouse_cookie = Locator.get_cookie(cookies.WAREHOUSE_COOKIE);
    	  if (warehouse_cookie.nearestWarehouse && warehouse_cookie.nearestWarehouse.catalog) { 
    		  currentWarehouse = (warehouse_cookie.nearestWarehouse.catalog).split('-')[0];
    		  console.log(currentWarehouse);
    	  }
      }
      console.log(warehouse.identifier);
      if (warehouse.identifier === currentWarehouse) {
    	return true;
      }  else{
    	  return false;
      }  
    },
    /**
     * Sets the appropriate view on the BING map based on whether or not we have 1 or more
     * location. If we only have one location displayed, we want to zoom out a bit.
     * @return {void}
     */
    set_view: function() {
      if (Locator.warehouses.length == 1) {
        let warehouse = Locator.warehouses[0];
        Locator.get_map().setView({
          center: new Microsoft.Maps.Location(warehouse.latitude, warehouse.longitude),
          zoom: 12,
        });
      } else {
        // Determine a bounding box to best view the results.
        let bounds = Microsoft.Maps.LocationRect.fromLocations(Locator.warehouses);
        // Use the locations from the results to calculate a bounding box.
        Locator.get_map().setView({
          bounds: bounds,
        });
      }
    },
    /**
     * Removes the pins from the BING map set in memory (Locator.map)
     * @param  {object} map The BING map set in memory (Locator.map)
     * @return {void}
     */
    remove_pins: function(map) {
      if (typeof map !== "undefined") {
    	for (let i = map.entities.getLength() - 1; i >= 0; i--) {
    	  let pushpin = map.entities.get(i);
    	  if (pushpin instanceof Microsoft.Maps.Pushpin) {
    	    map.entities.removeAt(i);
    	  }
    	}  
      }
    },
    /**
     * The onclick event handler for each pin. Does a series of things.
     * Sets and scrolls to the selected warehouse in the warehouse list,
     * Generates the appropriate infobox, and positions the map view to the
     * active warehouse.
     * @param  {object} args Object passed by the BING event setter. Has a bunch
     *                       of metadata containing warehouse stuff.
     * @return {void}
     */
    set_active_warehouse: function(args) {
      let metadata = args.target.metadata;
      /**
       * 	Warehouse tires locator map pins or warehouse row click must redirect
       * 	to tires page and not have a pop-up
       */
      if (Locator.is_tires) {
        // Commented below line as it as calling SetWarehouseNumberCmd command
        // twice breaking tires add to cart as Guest user.s
    	//Adding this change as part of STRY0139515 .
        Locator.set_tire_warehouse(metadata.index);
        return;
      }
      // Give the active warehouse a blue backgrounds
      $('#warehouse-list .table-row').removeClass('active');
      $('#warehouse-list .table-row[data-num=' + metadata.index + ']')
        .addClass('active');
    
      // remove aria-current attribute on previously active warehouse and give the active warehouse an aria-current="true" attribute
      $('a.warehouse-name').removeAttr('aria-current');
      $('.active').find('a.warehouse-name').attr('aria-current', 'true');

      // Scroll to the warehouse in the warehouse-list-container
      $('#warehouse-list-container')
        .scrollTo('.warehouse[data-num=' + metadata.index + ']', 100);    
      // Position center for the warehouse location
      Locator.get_map().setView({
        center: metadata.location,
      });
      // Create a Y offset so that the selected point
      // is center bottom to give infobox more real estate
      let offset = new Microsoft.Maps.Point(0, -80);
      let p = Locator.get_map().tryLocationToPixel(metadata.location);
      p.y += offset.y;
      Locator.get_map().setView({
        center: Locator.get_map().tryPixelToLocation(p),
      });
      // Show the corresponding infobox for the warehouse
      Locator.infobox.setOptions({
        location: metadata.location,
        htmlContent: COSTCO.util.tmpl('tmpl_locator_infobox', {
          is_tires: Locator.is_tires,
          is_transfer_warehouse: Locator.is_transfer_warehouse,
          is_pickup_warehouse: Locator.is_pickup_warehouse,
          locationName: metadata.warehouse.locationName,
          href: Locator.get_location_href(metadata.warehouse),
          distance: metadata.warehouse.distance.toFixed(2),
          measure: wcs.langId == '-1' ? 'mi' : 'km',
          has_Business_hour: wcs.storeId == '10301' ? true :false,		  
          warehouse_hours: metadata.warehouse.warehouseHours || [],
          upcomingHolidays: metadata.warehouse.upcomingHolidays,
          gas_prices: metadata.warehouse.gasPrices || [],
          work_hours_wh: metadata.warehouse.whUIhours,
          work_hours_gs: metadata.warehouse.gsUIhours,
          index: metadata.index,
          country: metadata.warehouse.country, 
        }),
        visible: true,
        offset: new Microsoft.Maps.Point(-120, 45),
      });
    },
    /**
     * Sets up a resusable infobox and then uses the warehouses in
     * memory (Locator.warehouses) to create pins on the map.
     * @param  {Boolean} isNewWarehouses Optional switch. If true, we don't
     *                                   create another instance of the infobox.
     *                                   (dynamically adding more pins to map flow)
     * @return {void}
     */
    add_pins: function(isNewWarehouses) {
      let pin;
      let pins = [];
      let pinSVGTemplateName = Locator.is_details() ?
        'tmpl_warehouse_pin' : 'tmpl_locator_pin';
      // Create a resusable infobox that displays on click if locator page
      if (!Locator.is_details() && !isNewWarehouses) {
        Locator.infobox = new Microsoft.Maps.Infobox(
          Locator.get_map().getCenter(), {
            visible: false,
          });
        Locator.infobox.setMap(Locator.get_map());
      }
      // Create a pushpin for each result.
      Locator.warehouses.forEach(function(warehouse, i) {
        let location = new Microsoft.Maps.Location(warehouse.latitude, warehouse.longitude);
        pin = new Microsoft.Maps.Pushpin(location, {
          icon: COSTCO.util.tmpl(pinSVGTemplateName, {
            number: i + 1,
            weight: 300,
            locationName: location,
          }),
          anchor: new Microsoft.Maps.Point(10.5185, 30),
        });
        pin.metadata = {
          warehouse: warehouse,
          location: location,
          index: i + 1,
        };
        if (!Locator.is_details()) {
          Microsoft.Maps.Events.addHandler(pin, 'click', Locator.set_active_warehouse);
        }
        
        pins.push(pin);
      });
      // Add the pins to the map
      Locator.get_map().entities.push(pins);
    },
    /**
     * The onsubmit event handler attached to #search-warehouse
     * that constitutes a new search
     * @return {void} [description]
     */
    find_warehouse: function() {
      Locator.get_map() ?
        Locator.search() :
        console.error('BING map not defined');
      document.activeElement.blur();
    },
    /**
     * The onclick event handler attached to the department filter
     * switch (Show/Hide Filter Options). If we're below 769 inner width,
     * pop a modal.
     * @return {void}
     */
    filter_switch: function() {
      let $filters = $('#filters');
      let $filterSwitch = $('#filter-option-switch');
      if ($(window).width() < 769) {
        if ($filters.hasClass('on')) {
          $filters.removeClass('on').addClass('off');
          $filterSwitch.text(messages.RWD_LOCATOR_SHOW_OPTIONS);
        }
        COSTCO.dialog.createExtDialogBox({
          title: messages.RWD_LOCATOR_FILTER_OPTIONS,
          body: COSTCO.util.tmpl('tmpl_locator_filters', {}),
          secondary: messages.JS_DIALOG_CANCEL,
          primary: messages.RWD_TOOLBAR_APPLY_FILTERS,
          primary_event: function() {
            $('.costcoModalBtn1').first().click();
            Locator.set_filter_state(Locator.get_filter_state(true));
            Locator.search();
          },
        });
        Locator.set_filter_state(Locator.get_filter_state());
        return;
      }
      if ($filters.hasClass('on')) {
        $filters.removeClass('on').addClass('off');
        $filterSwitch.text(messages.RWD_LOCATOR_SHOW_OPTIONS);
        $('#filter-option-switch').attr('aria-expanded','false');
      } else {
        $filters.removeClass('off').addClass('on');
        $filterSwitch.text(messages.RWD_LOCATOR_HIDE_OPTIONS);
        $('#filter-option-switch').attr('aria-expanded','true');
      }
      Locator.resize_elements();
    },
    /**
     * [description]
     * @param  {number} warehouse  user selected warehouse number
     * @param  {[type]} originator [description]
     * @return {void}
     */
    set_tire_shipto_home: function(warehouse, originator) {
      orderitemId = COSTCO.util.getParameterByName('orderitemId'),
        params = {
          requesttype: 'ajax',
          warehouseNumber: warehouse,
          originator: originator,
          fromPage: COSTCO.util.getParameterByName('fromPage') ?
            encodeURIComponent(COSTCO.util.getParameterByName('fromPage')) : 'WarehouseTiresLocatorView',
        };
      $('.tires-warehouse-select button').attr('disabled', 'disabled');

      // From checkout flow needs orderitemId
      if (orderitemId) {
        params.orderitemId = encodeURIComponent(orderitemId);
      }

      $.ajax({
          url: '/SetWarehouseNumberCmd?' + $.param(params),
          type: 'POST',
        })
        .done(function(data, textStatus, jqXHR) {
          data = COSTCO.util.scrubSpecialChars(data);
          try {
            data = JSON.parse(data);
            window.location.href = data.redirecturl.replace(/&amp;/g, '&');
          } catch (e) {
            console.error(e);
          }
        })
        .fail(function(jqXHR, textStatus, errorThrown) {
          console.error(errorThrown);
        })
        .always(function(data, textStatus, errorThrown) {
          try {
            data = JSON.parse(data);
            $('.tires-warehouse-select button')
              .removeAttr('disabled');
          } catch (e) {
            console.error(e);
          }
        });
    },
    
    add_viewmore_button: function() {
        // do not show 'view more' button if max results have reached
    	if(Locator.hasMoreWarehouses && !$('#view-more-warehouses').length && Locator.additionalWarehouses < 40){
    		let $warehouseListTable = $('#warehouse-list');
    		$warehouseListTable.append(COSTCO.util.tmpl('tmpl_locator_view_more', {}));
    	}    	
    },    
    
    /**
     * Add the selected my warehouse as part of Warehuse_whs cookie     
     * @return {void}
     */
    set_My_warehouse: function(index) {
        // do not show 'view more' button if max results have reached
    	    console.log(index);
    	    let warehouse = COSTCO.Locator.warehouses[index - 1];
    	    var warehouse_cookie,storeLoc_cookie,profile_populate;
    	    var orderedCookie,orderedCookienew,orderedProfileCookie;
    	    //-Start- Added IF condition of both warehousedelivery_whs and storelocation cookiee to keep structure for react publish
    	    if (!Locator.cookieExists(cookies.WAREHOUSE_COOKIE) &&  !Locator.cookieExists(cookies.STORELOCATION_COOKIE)){
    	    	profile_populate = {
    	    			 storeLocation: (warehouse.zipCode).split('-')[0],
    	    	        nearestWarehouse: {
    	    	          city: warehouse.locationName,
    	    	          catalog: warehouse.identifier+ '-wh',
    	    	        },
    	    	      };
    	    }else {
    	    	profile_populate = Locator.get_cookie(cookies.WAREHOUSE_COOKIE);
    	    	profile_populate.shipToLocation = Locator.get_cookie(cookies.INV_CHECK_POSTAL_CODE_COOKIE);
    	    	profile_populate.storeLocation = (warehouse.zipCode).split('-')[0];
    	        if (profile_populate.nearestWarehouse) {
    	        	profile_populate.nearestWarehouse.city = warehouse.locationName;
    	        	profile_populate.nearestWarehouse.catalog = warehouse.identifier+ '-wh';
    	        } else {
    	        	profile_populate.nearestWarehouse = {};
    	        	profile_populate.nearestWarehouse.city = warehouse.locationName;
    	        	profile_populate.nearestWarehouse.catalog = warehouse.identifier+ '-wh';
    	        }
    	    }
    	    ///-END- Added IF condition of both warehousedelivery_whs and storelocation cookiee to keep structure for react publish
    	    if (!Locator.cookieExists(cookies.WAREHOUSE_COOKIE)){
    	    	if(whscookiestructureEnableflag){
    	    		warehouse_cookie = {
    	    	    	        storeLocation: (warehouse.zipCode).split('-')[0],
    	    	    	        nearestWarehouse: {
    	    	    	          city: warehouse.locationName,
    	    	    	          catalog: warehouse.identifier+ '-wh',
    	    	    	        },
    	    	    	      };
    	    	}else{
    	    	warehouse_cookie = {
    	    	       // storeLocation: (warehouse.zipCode).split('-')[0],
    	    	        nearestWarehouse: {
    	    	         // city: warehouse.locationName,
    	    	          catalog: warehouse.identifier+ '-wh',
    	    	        },
    	    	      };
    	    }
    	    } else {
    	    	if(whscookiestructureEnableflag){
    	    	warehouse_cookie = Locator.get_cookie(cookies.WAREHOUSE_COOKIE);
    	    	console.log(warehouse_cookie);
    	    	warehouse_cookie.storeLocation = (warehouse.zipCode).split('-')[0];
    	        if (warehouse_cookie.nearestWarehouse) {
    	        	warehouse_cookie.nearestWarehouse.city = warehouse.locationName;
    	        	warehouse_cookie.nearestWarehouse.catalog = warehouse.identifier+ '-wh';
    	        } else {
    	        	warehouse_cookie.nearestWarehouse = {};
    	        	warehouse_cookie.nearestWarehouse.city = warehouse.locationName;
    	        	warehouse_cookie.nearestWarehouse.catalog = warehouse.identifier+ '-wh';
    	        }
    	    }else{
    	    	warehouse_cookie = Locator.get_cookie(cookies.WAREHOUSE_COOKIE);
    	    	console.log(warehouse_cookie);
    	    	//warehouse_cookie.storeLocation = (warehouse.zipCode).split('-')[0];
    	        if (warehouse_cookie.nearestWarehouse) {
    	        	//warehouse_cookie.nearestWarehouse.city = warehouse.locationName;
    	        	warehouse_cookie.nearestWarehouse.catalog = warehouse.identifier+ '-wh';
    	        } else {
    	        	warehouse_cookie.nearestWarehouse = {};
    	        	//warehouse_cookie.nearestWarehouse.city = warehouse.locationName;
    	        	warehouse_cookie.nearestWarehouse.catalog = warehouse.identifier+ '-wh';
    	        }
    	    }
    	    }
    	    //Added code to create new storelocation cookie from warehousedelivery_whs
    	    if (!Locator.cookieExists(cookies.STORELOCATION_COOKIE)){
    	    	storeLoc_cookie = {
    	    	        storeLocation: {
    	    	        	zip: (warehouse.zipCode).split('-')[0],
    	    	        	city: warehouse.locationName,
    	    	        },
    	    	      };
    	    } else {
    	    	storeLoc_cookie = Locator.get_cookie(cookies.STORELOCATION_COOKIE);
    	    	console.log(storeLoc_cookie);
    	    	
    	        if (storeLoc_cookie.storeLocation) {
    	        	storeLoc_cookie.storeLocation.city = warehouse.locationName;
    	        	storeLoc_cookie.storeLocation.zip = (warehouse.zipCode).split('-')[0];
    	        } else {
    	        	storeLoc_cookie.storeLocation = {};
    	        	storeLoc_cookie.storeLocation.city = warehouse.locationName;
    	        	storeLoc_cookie.storeLocation.zip = (warehouse.zipCode).split('-')[0];
    	        }
    	    }   	
    	    orderedCookie = Locator.orderCookie(warehouse_cookie);
    	    Locator.set_cookie(cookies.WAREHOUSE_COOKIE, JSON.stringify(orderedCookie), whsCookieExpiry);
    	    orderedCookienew = Locator.orderCookie(storeLoc_cookie);
    	    
    	    if(warehouse !== undefined && warehouse !== null){
	    	    if (localStorage.getItem("prWh") !== null ) {
	    	    	localStorage.setItem("prWh", JSON.stringify({
	    	    		 postalCode : (warehouse.zipCode).split('-')[0],
	    	    		 Id : warehouse.identifier+ '-wh'
	        	    }) );
	        	}
    	    }
    	    Locator.set_cookie(cookies.STORELOCATION_COOKIE, JSON.stringify(orderedCookienew), locationexpiryindays); 
    	    orderedProfileCookie = Locator.orderCookie(profile_populate);
    	    window.wioEventBus.publish('populateWarehouseCookie',orderedCookie );
    	    window.wioEventBus.publish('populateStoreLocationCookie',orderedCookienew );
    	    window.wioEventBus.publish('populateProfileCookie',orderedProfileCookie );
    	    let pickUpItemWarehouseLocator = $('#pickUpItemWarehouseLocator').val();
        	if ( pickUpItemWarehouseLocator == '' || pickUpItemWarehouseLocator == null || pickUpItemWarehouseLocator == 'false' ){
//        		COSTCO.Locator.updatePickUpWarehouse(warehouse.identifier);
        		window.location.reload(); 
        	}
    },
    
    view_more_warehouses: function(){
    	Locator.additionalWarehouses = Locator.additionalWarehouses + 10;
    	Locator.find_warehouse.call($('#warehouse-locator-search-form'));
    },
    
    expand_warehouse_details_accordion: function(){
    	$('.a11y-accordion').on('keydown', function(e){
    		if(e.keyCode == 32){
    			e.preventDefault();
        		$(this).click();
    	    }
    	});  
    },
    
    showWarehouseFlyout: function() {
    	COSTCO.dialog.close();
    	window.wioEventBus.publish('openWarehouseSideBar');
    }
    
  };

  if (typeof window.COSTCO == 'undefined') {
    window.COSTCO = {};
  }
  typeof window.COSTCO.Global == 'undefined' ?
    document.addEventListener('CostcoGlobalLoaded', Locator.loaded, false) :
    Locator.loaded();

  window.COSTCO.Locator = Locator;
});



// WEBPACK FOOTER //
// ./js/locator/locator.js